import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface Customer {
  id: string;
  phone: string;
  email?: string | null;
  name: string;
  joined_at: string;
  last_login: string;
  order_count?: number;
  total_spent?: number;
}

interface CustomersManagerProps {
  onToast: (msg: string, type?: 'success' | 'error') => void;
}

type SortKey = 'joined_at' | 'last_login' | 'name' | 'order_count' | 'total_spent';

// ── helpers ──
function startOfDay(d: Date) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}
function startOfWeek(d: Date) {
  const x = startOfDay(d);
  x.setDate(x.getDate() - x.getDay());
  return x;
}
function startOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth(), 1);
}
function startOfLastMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth() - 1, 1);
}

export default function CustomersManager({ onToast }: CustomersManagerProps) {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('joined_at');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [customerOrders, setCustomerOrders] = useState<{ orderRef: string; date: string; finalPrice: number; status: string }[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      // Use service role fallback: fetch with session header explicitly
      const { data: customersData, error } = await supabase
        .from('customers')
        .select('id, name, phone, email, joined_at, last_login, city, address, points')
        .order('joined_at', { ascending: false });

      if (error) {
        console.error('CustomersManager fetch error:', error.message, error.code, error.hint);
        onToast(`تعذّر تحميل العملاء: ${error.message}`, 'error');
        return;
      }

      if (!customersData || customersData.length === 0) {
        // Try fetching without order to rule out column issue
        const { data: fallback, error: fallbackErr } = await supabase
          .from('customers')
          .select('id, name, phone, email, joined_at, last_login');
        if (fallbackErr) {
          console.error('Fallback fetch error:', fallbackErr.message);
          onToast('تعذّر تحميل العملاء', 'error');
          return;
        }
        if (!fallback || fallback.length === 0) {
          setCustomers([]);
          return;
        }
        // Use fallback data
        const enrichedFallback: Customer[] = fallback.map(c => ({
          ...c,
          order_count: 0,
          total_spent: 0,
        }));
        setCustomers(enrichedFallback);
        return;
      }

      const { data: ordersData } = await supabase
        .from('orders')
        .select('phone, customer_id, final_price');

      // Build stats map by both phone AND customer_id
      const statsByPhone: Record<string, { count: number; total: number }> = {};
      const statsById: Record<string, { count: number; total: number }> = {};

      (ordersData ?? []).forEach((o: { phone: string; customer_id: string | null; final_price: number }) => {
        const amount = Number(o.final_price ?? 0);
        // By phone
        if (o.phone) {
          if (!statsByPhone[o.phone]) statsByPhone[o.phone] = { count: 0, total: 0 };
          statsByPhone[o.phone].count += 1;
          statsByPhone[o.phone].total += amount;
        }
        // By customer_id
        if (o.customer_id) {
          if (!statsById[o.customer_id]) statsById[o.customer_id] = { count: 0, total: 0 };
          statsById[o.customer_id].count += 1;
          statsById[o.customer_id].total += amount;
        }
      });

      const enriched: Customer[] = customersData.map(c => {
        // Prefer customer_id match, fallback to phone match
        const byId = statsById[c.id];
        const byPhone = c.phone && !c.phone.startsWith('email_') ? statsByPhone[c.phone] : undefined;
        const stats = byId ?? byPhone;
        return {
          ...c,
          order_count: stats?.count ?? 0,
          total_spent: stats?.total ?? 0,
        };
      });

      setCustomers(enriched);
    } finally {
      setLoading(false);
    }
  }, [onToast]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  const fetchCustomerOrders = useCallback(async (customer: Customer) => {
    setLoadingOrders(true);
    try {
      // Fetch by customer_id first (covers email-only users)
      const { data: byId } = await supabase
        .from('orders')
        .select('order_ref, created_at, final_price, status')
        .eq('customer_id', customer.id)
        .order('created_at', { ascending: false });

      // Also fetch by phone if customer has a real phone
      const hasRealPhone = customer.phone && !customer.phone.startsWith('email_') && customer.phone.trim() !== '';
      let byPhone: typeof byId = [];
      if (hasRealPhone) {
        const { data } = await supabase
          .from('orders')
          .select('order_ref, created_at, final_price, status')
          .eq('phone', customer.phone)
          .order('created_at', { ascending: false });
        byPhone = data ?? [];
      }

      // Merge and deduplicate by order_ref
      const allOrders = [...(byId ?? []), ...(byPhone ?? [])];
      const seen = new Set<string>();
      const unique = allOrders.filter(o => {
        if (seen.has(o.order_ref)) return false;
        seen.add(o.order_ref);
        return true;
      });

      // Sort by date desc
      unique.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());

      setCustomerOrders(
        unique.map(o => ({
          orderRef: o.order_ref,
          date: o.created_at,
          finalPrice: Number(o.final_price),
          status: o.status,
        }))
      );
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  const handleSelectCustomer = (c: Customer) => {
    setSelectedCustomer(c);
    fetchCustomerOrders(c);
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  };

  const filtered = customers.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    const name = (c.name ?? '').toLowerCase();
    const phone = (c.phone ?? '').toLowerCase();
    const email = (c.email ?? '').toLowerCase();
    return name.includes(q) || phone.includes(q) || email.includes(q);
  });

  const sorted = [...filtered].sort((a, b) => {
    let av: string | number = a[sortKey] ?? 0;
    let bv: string | number = b[sortKey] ?? 0;
    if (sortKey === 'joined_at' || sortKey === 'last_login') {
      av = av ? new Date(av as string).getTime() : 0;
      bv = bv ? new Date(bv as string).getTime() : 0;
    }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  // ── Growth stats ──
  const now = new Date();
  const weekStart = startOfWeek(now);
  const monthStart = startOfMonth(now);
  const lastMonthStart = startOfLastMonth(now);
  const todayStart = startOfDay(now);

  const newThisWeek = customers.filter(c => new Date(c.joined_at) >= weekStart).length;
  const newThisMonth = customers.filter(c => new Date(c.joined_at) >= monthStart).length;
  const newLastMonth = customers.filter(c => {
    const d = new Date(c.joined_at);
    return d >= lastMonthStart && d < monthStart;
  }).length;
  const newToday = customers.filter(c => new Date(c.joined_at) >= todayStart).length;

  const monthGrowth = newLastMonth === 0
    ? newThisMonth > 0 ? 100 : 0
    : Math.round(((newThisMonth - newLastMonth) / newLastMonth) * 100);

  // ── Last 30 days daily chart data ──
  const last30Days = Array.from({ length: 30 }, (_, i) => {
    const d = new Date(now);
    d.setDate(d.getDate() - (29 - i));
    return startOfDay(d);
  });

  const dailyCounts = last30Days.map(dayStart => {
    const dayEnd = new Date(dayStart);
    dayEnd.setDate(dayEnd.getDate() + 1);
    return customers.filter(c => {
      const d = new Date(c.joined_at);
      return d >= dayStart && d < dayEnd;
    }).length;
  });

  const maxDaily = Math.max(...dailyCounts, 1);

  // ── Last 6 months bar data ──
  const last6Months = Array.from({ length: 6 }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - (5 - i), 1);
    return d;
  });

  const monthlyCounts = last6Months.map(mStart => {
    const mEnd = new Date(mStart.getFullYear(), mStart.getMonth() + 1, 1);
    return customers.filter(c => {
      const d = new Date(c.joined_at);
      return d >= mStart && d < mEnd;
    }).length;
  });
  const maxMonthly = Math.max(...monthlyCounts, 1);

  const monthNames = ['يناير','فبراير','مارس','أبريل','مايو','يونيو','يوليو','أغسطس','سبتمبر','أكتوبر','نوفمبر','ديسمبر'];

  // ── General stats ──
  const totalCustomers = customers.length;
  const totalOrders = customers.reduce((s, c) => s + (c.order_count ?? 0), 0);
  const totalRevenue = customers.reduce((s, c) => s + (c.total_spent ?? 0), 0);
  const activeCustomers = customers.filter(c => (c.order_count ?? 0) > 0).length;

  const statusLabel: Record<string, { label: string; cls: string }> = {
    pending:   { label: 'قيد الانتظار', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    confirmed: { label: 'مؤكد',         cls: 'bg-green-50 text-green-700 border-green-200' },
    shipped:   { label: 'تم الشحن',     cls: 'bg-indigo-50 text-indigo-700 border-indigo-200' },
    delivered: { label: 'تم التوصيل',   cls: 'bg-teal-50 text-teal-700 border-teal-200' },
    cancelled: { label: 'ملغي',          cls: 'bg-red-50 text-red-600 border-red-200' },
  };

  const SortIcon = ({ k }: { k: SortKey }) => (
    <i className={`text-xs mr-0.5 ${sortKey === k ? 'ri-arrow-up-down-line text-[#1a1a1a]' : 'ri-arrow-up-down-line text-gray-300'}`}></i>
  );

  return (
    <div className="flex flex-col gap-5">

      {/* ── Growth Highlight Cards ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {/* Today */}
        <div className="bg-white rounded-xl p-4 border border-gray-100 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="w-8 h-8 flex items-center justify-center bg-green-50 rounded-lg">
              <i className="ri-user-add-line text-green-600 text-sm"></i>
            </div>
            <span className="text-[10px] font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">اليوم</span>
          </div>
          <p className="text-2xl font-black text-[#1a1a1a]">{newToday}</p>
          <p className="text-xs text-gray-400">عميل جديد اليوم</p>
        </div>

        {/* This Week */}
        <div className="bg-white rounded-xl p-4 border border-gray-100 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg">
              <i className="ri-calendar-check-line text-amber-600 text-sm"></i>
            </div>
            <span className="text-[10px] font-bold text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">هذا الأسبوع</span>
          </div>
          <p className="text-2xl font-black text-[#1a1a1a]">{newThisWeek}</p>
          <p className="text-xs text-gray-400">عميل جديد هذا الأسبوع</p>
        </div>

        {/* This Month */}
        <div className="bg-white rounded-xl p-4 border border-gray-100 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="w-8 h-8 flex items-center justify-center bg-[#1a1a1a]/5 rounded-lg">
              <i className="ri-calendar-2-line text-[#1a1a1a] text-sm"></i>
            </div>
            <div className="flex items-center gap-1">
              {monthGrowth !== 0 && (
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full flex items-center gap-0.5 ${
                  monthGrowth > 0 ? 'text-green-600 bg-green-50' : 'text-red-500 bg-red-50'
                }`}>
                  <i className={`text-[10px] ${monthGrowth > 0 ? 'ri-arrow-up-line' : 'ri-arrow-down-line'}`}></i>
                  {Math.abs(monthGrowth)}%
                </span>
              )}
            </div>
          </div>
          <p className="text-2xl font-black text-[#1a1a1a]">{newThisMonth}</p>
          <p className="text-xs text-gray-400">عميل جديد هذا الشهر</p>
          {newLastMonth > 0 && (
            <p className="text-[10px] text-gray-400">
              الشهر الماضي: <span className="font-bold text-gray-500">{newLastMonth}</span>
            </p>
          )}
        </div>

        {/* Total */}
        <div className="bg-[#1a1a1a] rounded-xl p-4 flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div className="w-8 h-8 flex items-center justify-center bg-white/10 rounded-lg">
              <i className="ri-group-line text-white text-sm"></i>
            </div>
            <span className="text-[10px] font-bold text-white/60 bg-white/10 px-2 py-0.5 rounded-full">الإجمالي</span>
          </div>
          <p className="text-2xl font-black text-white">{totalCustomers}</p>
          <p className="text-xs text-white/50">إجمالي العملاء المسجلين</p>
        </div>
      </div>

      {/* ── Charts Row ── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">

        {/* Daily registrations — last 30 days sparkline */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-bold text-[#1a1a1a]">التسجيلات اليومية</p>
              <p className="text-xs text-gray-400 mt-0.5">آخر 30 يوم</p>
            </div>
            <div className="w-8 h-8 flex items-center justify-center bg-green-50 rounded-lg">
              <i className="ri-line-chart-line text-green-600 text-sm"></i>
            </div>
          </div>

          {/* Sparkline bars */}
          <div className="flex items-end gap-0.5 h-20">
            {dailyCounts.map((count, i) => {
              const heightPct = maxDaily === 0 ? 0 : (count / maxDaily) * 100;
              const isToday = i === 29;
              return (
                <div
                  key={i}
                  className="flex-1 flex flex-col items-center justify-end group relative"
                  style={{ height: '100%' }}
                >
                  <div
                    className={`w-full rounded-sm transition-all ${
                      isToday ? 'bg-green-500' : count > 0 ? 'bg-gray-300 group-hover:bg-gray-400' : 'bg-gray-100'
                    }`}
                    style={{ height: `${Math.max(heightPct, count > 0 ? 8 : 2)}%` }}
                  ></div>
                  {/* Tooltip */}
                  {count > 0 && (
                    <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-[#1a1a1a] text-white text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                      {count} عميل
                      <br />
                      {last30Days[i].toLocaleDateString('ar-SA', { month: 'short', day: 'numeric' })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* X-axis labels */}
          <div className="flex justify-between mt-2">
            <span className="text-[10px] text-gray-400">
              {last30Days[0].toLocaleDateString('ar-SA', { month: 'short', day: 'numeric' })}
            </span>
            <span className="text-[10px] text-green-600 font-bold">اليوم</span>
          </div>
        </div>

        {/* Monthly registrations — last 6 months */}
        <div className="bg-white rounded-xl border border-gray-100 p-5">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-sm font-bold text-[#1a1a1a]">التسجيلات الشهرية</p>
              <p className="text-xs text-gray-400 mt-0.5">آخر 6 أشهر</p>
            </div>
            <div className="w-8 h-8 flex items-center justify-center bg-amber-50 rounded-lg">
              <i className="ri-bar-chart-2-line text-amber-600 text-sm"></i>
            </div>
          </div>

          <div className="flex items-end gap-2 h-20">
            {monthlyCounts.map((count, i) => {
              const heightPct = maxMonthly === 0 ? 0 : (count / maxMonthly) * 100;
              const isCurrent = i === 5;
              return (
                <div
                  key={i}
                  className="flex-1 flex flex-col items-center gap-1 group relative"
                  style={{ height: '100%' }}
                >
                  <div className="flex-1 flex items-end w-full">
                    <div
                      className={`w-full rounded-md transition-all ${
                        isCurrent ? 'bg-[#1a1a1a]' : 'bg-gray-200 group-hover:bg-gray-300'
                      }`}
                      style={{ height: `${Math.max(heightPct, count > 0 ? 10 : 3)}%` }}
                    ></div>
                  </div>
                  {/* Tooltip */}
                  {count > 0 && (
                    <div className="absolute bottom-full mb-1 left-1/2 -translate-x-1/2 bg-[#1a1a1a] text-white text-[9px] px-1.5 py-0.5 rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                      {count} عميل
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* X-axis labels */}
          <div className="flex gap-2 mt-2">
            {last6Months.map((m, i) => (
              <div key={i} className="flex-1 text-center">
                <span className={`text-[10px] ${i === 5 ? 'text-[#1a1a1a] font-bold' : 'text-gray-400'}`}>
                  {monthNames[m.getMonth()].slice(0, 3)}
                </span>
                <p className={`text-[10px] font-bold mt-0.5 ${i === 5 ? 'text-[#1a1a1a]' : 'text-gray-500'}`}>
                  {monthlyCounts[i]}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── General Stats ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'إجمالي العملاء',   value: totalCustomers,                                    icon: 'ri-group-line',          color: 'text-[#1a1a1a]' },
          { label: 'عملاء نشطون',      value: activeCustomers,                                   icon: 'ri-user-star-line',       color: 'text-green-600' },
          { label: 'إجمالي الطلبات',   value: totalOrders,                                       icon: 'ri-shopping-bag-3-line',  color: 'text-amber-600' },
          { label: 'إجمالي الإيرادات', value: `${totalRevenue.toLocaleString('ar-SA')} ر.س`,    icon: 'ri-money-dollar-circle-line', color: 'text-[#1a1a1a]' },
        ].map(s => (
          <div key={s.label} className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="w-8 h-8 flex items-center justify-center bg-gray-50 rounded-lg mb-2">
              <i className={`${s.icon} text-sm ${s.color}`}></i>
            </div>
            <p className={`text-lg font-bold ${s.color}`}>{s.value}</p>
            <p className="text-xs text-gray-400 mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      {/* ── Table card ── */}
      <div className="bg-white rounded-2xl border border-gray-100">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <h2 className="font-bold text-[#1a1a1a] text-base">قائمة العملاء</h2>
            <span className="flex items-center gap-1 bg-green-50 border border-green-200 text-green-700 text-[10px] font-bold px-2 py-0.5 rounded-full">
              <i className="ri-database-2-line text-xs"></i>
              Supabase
            </span>
          </div>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <div className="flex-1 sm:w-56 relative">
              <i className="ri-search-line absolute top-1/2 -translate-y-1/2 right-3 text-gray-300 text-sm"></i>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="بحث بالاسم أو الجوال..."
                className="w-full border border-gray-200 rounded-lg pr-8 pl-3 py-2 text-sm outline-none focus:border-[#1a1a1a] transition-colors"
              />
            </div>
            <button
              onClick={fetchCustomers}
              className="w-9 h-9 flex items-center justify-center border border-gray-200 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
              title="تحديث"
            >
              <i className="ri-refresh-line text-sm"></i>
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-10 h-10 border-2 border-gray-200 border-t-[#1a1a1a] rounded-full animate-spin"></div>
              <p className="text-sm text-gray-400">جاري تحميل العملاء...</p>
            </div>
          ) : sorted.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
              <div className="w-14 h-14 flex items-center justify-center bg-gray-50 rounded-full">
                <i className="ri-group-line text-2xl text-gray-300"></i>
              </div>
              <p className="text-sm text-gray-400">
                {search ? 'لا توجد نتائج للبحث' : 'لا يوجد عملاء مسجلون بعد'}
              </p>
            </div>
          ) : (
            <table className="w-full text-sm" dir="rtl">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-right text-xs font-medium text-gray-400 pb-3 px-6 pt-3">العميل</th>
                  <th
                    className="text-right text-xs font-medium text-gray-400 pb-3 pt-3 cursor-pointer hover:text-gray-600 whitespace-nowrap"
                    onClick={() => handleSort('order_count')}
                  >
                    الطلبات <SortIcon k="order_count" />
                  </th>
                  <th
                    className="text-right text-xs font-medium text-gray-400 pb-3 pt-3 cursor-pointer hover:text-gray-600 whitespace-nowrap"
                    onClick={() => handleSort('total_spent')}
                  >
                    الإنفاق <SortIcon k="total_spent" />
                  </th>
                  <th
                    className="text-right text-xs font-medium text-gray-400 pb-3 pt-3 cursor-pointer hover:text-gray-600 whitespace-nowrap"
                    onClick={() => handleSort('joined_at')}
                  >
                    تاريخ التسجيل <SortIcon k="joined_at" />
                  </th>
                  <th
                    className="text-right text-xs font-medium text-gray-400 pb-3 pt-3 cursor-pointer hover:text-gray-600 whitespace-nowrap"
                    onClick={() => handleSort('last_login')}
                  >
                    آخر دخول <SortIcon k="last_login" />
                  </th>
                  <th className="pb-3 pt-3 px-4"></th>
                </tr>
              </thead>
              <tbody>
                {sorted.map(c => {
                  const isNew = new Date(c.joined_at) >= weekStart;
                  return (
                    <tr
                      key={c.id}
                      className="border-b border-gray-50 hover:bg-gray-50 transition-colors group cursor-pointer"
                      onClick={() => handleSelectCustomer(c)}
                    >
                      <td className="py-3 px-6">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 flex items-center justify-center bg-gray-100 rounded-xl flex-shrink-0 font-bold text-[#1a1a1a] text-sm">
                            {c.name.charAt(0)}
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="font-medium text-[#1a1a1a] text-sm">{c.name}</p>
                              {isNew && (
                                <span className="text-[9px] font-black text-green-600 bg-green-50 border border-green-200 px-1.5 py-0.5 rounded-full whitespace-nowrap">
                                  جديد
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-400 mt-0.5 tracking-wider">
                            {c.phone && !c.phone.startsWith('email_') && c.phone.trim() !== ''
                              ? c.phone
                              : c.email ?? '—'}
                          </p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3">
                        <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-lg ${
                          (c.order_count ?? 0) > 0 ? 'bg-amber-50 text-amber-700' : 'bg-gray-50 text-gray-400'
                        }`}>
                          <i className="ri-shopping-bag-3-line text-xs"></i>
                          {c.order_count ?? 0}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className="font-semibold text-[#1a1a1a] text-sm">
                          {(c.total_spent ?? 0) > 0
                            ? `${(c.total_spent ?? 0).toLocaleString('ar-SA')} ر.س`
                            : <span className="text-gray-300 font-normal">—</span>
                          }
                        </span>
                      </td>
                      <td className="py-3">
                        <span className="text-xs text-gray-500">
                          {new Date(c.joined_at).toLocaleDateString('ar-SA', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </span>
                      </td>
                      <td className="py-3">
                        <span className="text-xs text-gray-400">
                          {new Date(c.last_login).toLocaleDateString('ar-SA', { year: 'numeric', month: 'short', day: 'numeric' })}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <button
                          onClick={e => { e.stopPropagation(); handleSelectCustomer(c); }}
                          className="opacity-0 group-hover:opacity-100 transition-opacity w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-[#1a1a1a] cursor-pointer"
                        >
                          <i className="ri-eye-line text-sm"></i>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="px-6 py-3 border-t border-gray-50 flex items-center justify-between">
          <p className="text-xs text-gray-400">{sorted.length} عميل</p>
          <div className="flex items-center gap-1.5 text-xs text-green-600">
            <i className="ri-cloud-line text-sm"></i>
            <span>محفوظ في قاعدة البيانات</span>
          </div>
        </div>
      </div>

      {/* ── Customer Detail Drawer ── */}
      {selectedCustomer && (
        <div className="fixed inset-0 z-50 flex" dir="rtl">
          <div className="flex-1 bg-black/30" onClick={() => setSelectedCustomer(null)}></div>
          <div className="w-full max-w-md bg-white h-full overflow-y-auto flex flex-col">
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 sticky top-0 bg-white z-10">
              <h3 className="font-bold text-[#1a1a1a] text-base">تفاصيل العميل</h3>
              <button
                onClick={() => setSelectedCustomer(null)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <div className="flex-1 px-6 py-5 flex flex-col gap-5">
              <div className="flex items-center gap-4 bg-gray-50 rounded-2xl p-4">
                <div className="w-14 h-14 flex items-center justify-center bg-[#1a1a1a] rounded-2xl flex-shrink-0">
                  <span className="text-white text-xl font-black">{selectedCustomer.name.charAt(0)}</span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-bold text-[#1a1a1a] text-base truncate">{selectedCustomer.name}</p>
                    {new Date(selectedCustomer.joined_at) >= weekStart && (
                      <span className="text-[10px] font-black text-green-600 bg-green-50 border border-green-200 px-2 py-0.5 rounded-full whitespace-nowrap">
                        عميل جديد هذا الأسبوع
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-gray-500 tracking-wider mt-0.5">
                    {selectedCustomer.phone && !selectedCustomer.phone.startsWith('email_') && selectedCustomer.phone.trim() !== ''
                      ? selectedCustomer.phone
                      : selectedCustomer.email ?? '—'}
                  </p>
                  <div className="flex items-center gap-1.5 mt-1.5">
                    <div className="w-2 h-2 rounded-full bg-green-400"></div>
                    <span className="text-xs text-green-600 font-medium">حساب مُتحقق</span>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-lg font-black text-amber-600">{selectedCustomer.order_count ?? 0}</p>
                  <p className="text-[10px] text-gray-400 mt-0.5">طلب</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-base font-black text-[#1a1a1a]">
                    {(selectedCustomer.total_spent ?? 0) > 0
                      ? `${(selectedCustomer.total_spent ?? 0).toLocaleString('ar-SA')}`
                      : '—'}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5">ر.س إنفاق</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-3 text-center">
                  <p className="text-base font-black text-green-600">
                    {(selectedCustomer.order_count ?? 0) > 0
                      ? Math.round((selectedCustomer.total_spent ?? 0) / (selectedCustomer.order_count ?? 1)).toLocaleString('ar-SA')
                      : '—'}
                  </p>
                  <p className="text-[10px] text-gray-400 mt-0.5">متوسط الطلب</p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-white rounded-lg flex-shrink-0">
                    <i className="ri-calendar-line text-gray-400 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">تاريخ التسجيل</p>
                    <p className="text-sm font-medium text-[#1a1a1a] mt-0.5">
                      {new Date(selectedCustomer.joined_at).toLocaleDateString('ar-SA', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 bg-gray-50 rounded-xl px-4 py-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-white rounded-lg flex-shrink-0">
                    <i className="ri-time-line text-gray-400 text-sm"></i>
                  </div>
                  <div>
                    <p className="text-xs text-gray-400">آخر دخول</p>
                    <p className="text-sm font-medium text-[#1a1a1a] mt-0.5">
                      {new Date(selectedCustomer.last_login).toLocaleDateString('ar-SA', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                </div>
              </div>

              {selectedCustomer.phone && !selectedCustomer.phone.startsWith('email_') && selectedCustomer.phone.trim() !== '' ? (
                <a
                  href={`https://wa.me/966${selectedCustomer.phone.replace(/^0/, '')}?text=${encodeURIComponent(`مرحباً ${selectedCustomer.name}، نتواصل معك من متجر الإبداع`)}`}
                  target="_blank"
                  rel="nofollow noreferrer"
                  className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-bold py-3 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-whatsapp-line text-base"></i>
                  تواصل عبر واتساب
                </a>
              ) : selectedCustomer.email ? (
                <a
                  href={`mailto:${selectedCustomer.email}`}
                  className="flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 text-white font-bold py-3 rounded-xl text-sm transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-mail-send-line text-base"></i>
                  تواصل عبر البريد الإلكتروني
                </a>
              ) : null}

              <div>
                <h4 className="font-bold text-[#1a1a1a] text-sm mb-3 flex items-center gap-2">
                  <i className="ri-shopping-bag-3-line text-amber-500"></i>
                  طلبات العميل
                </h4>
                {loadingOrders ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="w-8 h-8 border-2 border-gray-200 border-t-[#1a1a1a] rounded-full animate-spin"></div>
                  </div>
                ) : customerOrders.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-8 gap-2 bg-gray-50 rounded-xl">
                    <i className="ri-inbox-line text-2xl text-gray-300"></i>
                    <p className="text-xs text-gray-400">لا توجد طلبات بعد</p>
                  </div>
                ) : (
                  <div className="flex flex-col gap-2">
                    {customerOrders.map(o => (
                      <div key={o.orderRef} className="flex items-center justify-between bg-gray-50 rounded-xl px-4 py-3">
                        <div>
                          <p className="text-xs font-bold text-[#1a1a1a]">#{o.orderRef}</p>
                          <p className="text-[10px] text-gray-400 mt-0.5">
                            {new Date(o.date).toLocaleDateString('ar-SA', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${statusLabel[o.status]?.cls ?? 'bg-gray-50 text-gray-500 border-gray-200'}`}>
                            {statusLabel[o.status]?.label ?? o.status}
                          </span>
                          <span className="text-sm font-bold text-[#1a1a1a]">
                            {o.finalPrice.toLocaleString('ar-SA')} ر.س
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
